import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCAD, formatClosing, KANBAN_STAGES, stageMeta, type AppStage,
} from '../lib/format';
import { pushToast } from '../hooks/useToasts';
import { isInsideEditable } from '../hooks/useGlobalShortcuts';

interface AppRow {
  id: string;
  match_id: string;
  partner_profile_id: string;
  grant_program_id: string;
  funding_ask_cad: number;
  required_match_cash: number;
  required_match_inkind: number;
  match_committed_cad: number;
  match_source: string | null;
  stage: AppStage;
  submitted_at: number | null;
  decision_at: number | null;
  awarded_amount_cad: number | null;
  interim_report_due_at: number | null;
  final_report_due_at: number | null;
  created_at: number;
  updated_at: number;
  grant_title: string;
  grant_source_url: string;
  grant_closes_at: number | null;
  funder_name: string;
  funder_level: string;
  profile_name: string;
  profile_slug: string;
}

const KEY_TO_STAGE: Record<string, AppStage> = {
  '1': 'drafting', '2': 'submitted', '3': 'awaiting', '4': 'won', '5': 'lost',
};

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.get<AppRow[]>('/applications'),
  });

  const byStage = useMemo(() => {
    const grouped: Record<AppStage, AppRow[]> = {
      drafting: [], submitted: [], awaiting: [], won: [], lost: [], withdrawn: [],
    };
    for (const a of listQ.data?.data ?? []) grouped[a.stage]?.push(a);
    return grouped;
  }, [listQ.data]);

  // Move mutation — optimistic across the kanban query.
  const moveMut = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: AppStage }) =>
      api.patch(`/applications/${id}`, { stage }),
    onMutate: async ({ id, stage }) => {
      await qc.cancelQueries({ queryKey: ['applications'] });
      const prev = qc.getQueryData<{ data: AppRow[] }>(['applications']);
      if (prev) {
        qc.setQueryData<{ data: AppRow[] }>(['applications'], {
          ...prev,
          data: prev.data.map((a) => (a.id === id ? { ...a, stage } : a)),
        });
      }
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['applications'], ctx.prev);
      pushToast(err instanceof ApiError ? err.message : 'Move failed', 'error');
    },
    onSuccess: (_data, vars) => {
      pushToast(`Moved to ${stageMeta[vars.stage].label}`, 'success', 1600);
    },
  });

  // Drag state — track the dragged id so dragover can highlight columns.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<AppStage | null>(null);

  // Keyboard nav: ←/→ moves focused card between adjacent stages,
  // 1–5 jumps to a specific column. Card focus is by data-app-id.
  const focusedRef = useRef<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isInsideEditable(e.target)) return;
      const id = focusedRef.current;
      if (!id) return;
      const row = (listQ.data?.data ?? []).find((a) => a.id === id);
      if (!row) return;
      const idx = KANBAN_STAGES.indexOf(row.stage as typeof KANBAN_STAGES[number]);
      if (idx === -1) return;

      if (e.key === 'ArrowRight' && idx < KANBAN_STAGES.length - 1) {
        e.preventDefault();
        moveMut.mutate({ id, stage: KANBAN_STAGES[idx + 1] });
        return;
      }
      if (e.key === 'ArrowLeft' && idx > 0) {
        e.preventDefault();
        moveMut.mutate({ id, stage: KANBAN_STAGES[idx - 1] });
        return;
      }
      const target = KEY_TO_STAGE[e.key];
      if (target && target !== row.stage) {
        e.preventDefault();
        moveMut.mutate({ id, stage: target });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [listQ.data, moveMut]);

  if (listQ.error instanceof ApiError && listQ.error.status === 401) {
    navigate({ to: '/login' });
    return null;
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-4 py-3 border-b border-neutral-200 flex items-center gap-3">
        <h1 className="text-base font-semibold">Applications</h1>
        <p className="text-xs text-neutral-600">
          Drag cards between columns, or focus a card and use{' '}
          <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded border border-neutral-200">←</kbd>{' '}
          <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded border border-neutral-200">→</kbd>{' '}
          or{' '}
          <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded border border-neutral-200">1</kbd>–
          <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded border border-neutral-200">5</kbd>{' '}
          to change stage.
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-x-auto">
        <div
          role="list"
          aria-label="Application stages"
          className="grid grid-cols-5 gap-3 p-3 min-w-[1100px] h-full"
        >
          {KANBAN_STAGES.map((stage) => (
            <Column
              key={stage}
              stage={stage}
              rows={byStage[stage]}
              isDragOver={dragOver === stage}
              onDragOverColumn={() => setDragOver(stage)}
              onDragLeaveColumn={() => setDragOver((s) => (s === stage ? null : s))}
              onDrop={() => {
                if (dragId) {
                  const row = (listQ.data?.data ?? []).find((a) => a.id === dragId);
                  if (row && row.stage !== stage) moveMut.mutate({ id: dragId, stage });
                }
                setDragId(null);
                setDragOver(null);
              }}
              onCardFocus={(id) => { focusedRef.current = id; }}
              onCardBlur={(id) => { if (focusedRef.current === id) focusedRef.current = null; }}
              onDragStart={(id) => setDragId(id)}
              onDragEnd={() => { setDragId(null); setDragOver(null); }}
            />
          ))}
        </div>
      </div>

      {listQ.isLoading && <div className="px-4 py-3 text-sm text-neutral-600">Loading applications — a moment.</div>}
      {!listQ.isLoading && (listQ.data?.data ?? []).length === 0 && (
        <div className="px-4 py-6 text-sm text-neutral-700">
          No applications yet. Promote a match from{' '}
          <Link to="/" className="underline text-accent">Triage</Link>{' '}
          to create one.
        </div>
      )}
    </div>
  );
}

function Column({
  stage, rows, isDragOver, onDragOverColumn, onDragLeaveColumn, onDrop,
  onCardFocus, onCardBlur, onDragStart, onDragEnd,
}: {
  stage: typeof KANBAN_STAGES[number];
  rows: AppRow[];
  isDragOver: boolean;
  onDragOverColumn: () => void;
  onDragLeaveColumn: () => void;
  onDrop: () => void;
  onCardFocus: (id: string) => void;
  onCardBlur: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
}) {
  const meta = stageMeta[stage];
  const totalAsk = rows.reduce((acc, r) => acc + (r.funding_ask_cad ?? 0), 0);
  return (
    <div
      role="listitem"
      aria-label={`${meta.label} column, ${rows.length} application${rows.length === 1 ? '' : 's'}`}
      onDragOver={(e) => { e.preventDefault(); onDragOverColumn(); }}
      onDragLeave={onDragLeaveColumn}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      className={
        'flex flex-col min-h-0 rounded-md border ' +
        (isDragOver
          ? 'border-accent ring-2 ring-accent/30 bg-accent/5'
          : meta.columnCls)
      }
    >
      <header className="px-3 py-2 border-b border-inherit flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={'text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + meta.cls}>
            {meta.label}
          </span>
          <span className="text-[11px] text-neutral-700">{rows.length}</span>
        </div>
        <span className="text-[11px] text-neutral-700" title="Total funding ask in this column">
          {formatCAD(totalAsk)}
        </span>
      </header>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {rows.length === 0 && (
          <p className="text-[11px] text-neutral-700 px-1 py-3">Empty.</p>
        )}
        {rows.map((row) => (
          <Card
            key={row.id}
            row={row}
            onFocus={() => onCardFocus(row.id)}
            onBlur={() => onCardBlur(row.id)}
            onDragStart={() => onDragStart(row.id)}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>
    </div>
  );
}

function Card({
  row, onFocus, onBlur, onDragStart, onDragEnd,
}: {
  row: AppRow;
  onFocus: () => void;
  onBlur: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div
      data-app-id={row.id}
      role="button"
      tabIndex={0}
      aria-roledescription="Draggable application card"
      aria-label={`${row.grant_title}. Stage ${stageMeta[row.stage].label}. Funding ask ${formatCAD(row.funding_ask_cad)}. ${row.profile_name}.`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        // Some browsers require non-empty data to fire drag events.
        e.dataTransfer.setData('text/plain', row.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={() => navigate({ to: '/applications/$id', params: { id: row.id } })}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          navigate({ to: '/applications/$id', params: { id: row.id } });
        }
      }}
      className="block w-full text-left bg-white border border-neutral-200 rounded-md p-2.5 shadow-sm hover:border-neutral-300 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-accent active:cursor-grabbing"
    >
      <div className="text-sm font-medium text-neutral-900 line-clamp-2">{row.grant_title}</div>
      <div className="mt-1 text-[11px] text-neutral-700 truncate">
        {row.funder_name} · <span className="uppercase tracking-wide">{row.funder_level}</span>
      </div>
      <div className="mt-1 text-[11px] text-neutral-600 truncate">For: {row.profile_name}</div>
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="text-neutral-700">Ask {formatCAD(row.funding_ask_cad)}</span>
        <span className="text-neutral-700">Match {formatCAD(row.match_committed_cad)}</span>
      </div>
      {row.grant_closes_at != null && (
        <div className="mt-1 text-[10px] text-neutral-700">{formatClosing(row.grant_closes_at)}</div>
      )}
    </div>
  );
}
